import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AppInjector } from '../../app-injector';
import { IntegrationItemServiceInf, StatusString } from '../interfaces/integrations';
import { TranslateService } from '@ngx-translate/core';
import { AdminService } from 'src/app/core/services/admin.service';
import { Account } from '@mitel/cloudlink-sdk';
import { SpinnerService } from './spinner.service';
import { ConfirmationDialogComponent } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { AccountsService } from './accounts.service';
import { RBACAuthGuardService } from '../guards/rbac-auth-guard.service';
import { FeatureToggleService } from './feature-toggle.service';
import { FeatureFlags } from '../interfaces/feature-flags';
import { CMcomDialogComponent } from '../../modules/integrations-applications/components/integ-config-dialogs/cmcom-dialog/cmcom-dialog.component'
import { WorkflowService } from './workflow.service';

@Injectable({
	providedIn: 'root'
})

// this service will hide whatever implementation we eventually have for gettinng the list of integrations
// types. Right now it is static
export class IntegCMcomService implements IntegrationItemServiceInf {

	private adminService: AdminService;
	private translateService: TranslateService;
	private spinnerService: SpinnerService;
	public dialog: MatDialog;
	private accountsService: AccountsService;
	private rbacAuthGuardService: RBACAuthGuardService;
	private featureToggleService: FeatureToggleService;
	private workflowService: WorkflowService;

	readonly miccTag = 'MICCB_CMCOM';

	constructor() {
	}

	initServices() {
		if (!this.adminService)
			this.adminService = AppInjector.injector.get(AdminService);

		if (!this.translateService)
			this.translateService = AppInjector.injector.get(TranslateService);

		if (!this.spinnerService)
			this.spinnerService = AppInjector.injector.get(SpinnerService);

		if (!this.accountsService)
			this.accountsService = AppInjector.injector.get(AccountsService);

		if (!this.dialog)
			this.dialog = AppInjector.injector.get(MatDialog);

		if (!this.rbacAuthGuardService)
			this.rbacAuthGuardService = AppInjector.injector.get(RBACAuthGuardService);

		if (!this.featureToggleService)
			this.featureToggleService = AppInjector.injector.get(FeatureToggleService);

		if (!this.workflowService)
			this.workflowService = AppInjector.injector.get(WorkflowService);
	}

	getConfigurationDialog(): any {
		return CMcomDialogComponent;
	}

	isSetupComplete(account: Account): Promise<boolean> {
		this.initServices();
		return this.adminService.getAccountTags(account.accountId).then(tags => {
			if (tags && tags.CMcom) {
				if (tags.CMcom.enabled == true && tags.CMcom.connectionId !== undefined)
					return true;
				return false;
			}
			else
				return false;
		})
			.catch(err => {
				return false;
			});
	}

	handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any> {
		// add the policy on enable, show dialog and remove the policy on remove
		this.initServices();
		if (state === false) {
			return this.adminService.deleteAccountTag(account.accountId, 'CMcom').then(async data => {
				await this.workflowService.deleteActivitiesByTagId(this.miccTag);
				return;
			})
				.catch(async err => {
					// an error is thrown if no tag exists. That is OK!
					if (err.statusCode === 404) {
						await this.workflowService.deleteActivitiesByTagId(this.miccTag);
					}
					return;
				})
		}
		else {
			console.log("Save the changes to the integration - when successful, close the dialog");
			let dataChanges = {
				tags: {
					CMcom: {
						enabled: true
					}
				}
			}
			console.log("about to save the integration changes", dataChanges);

			return this.adminService.updateAccount(account.accountId, dataChanges).then(result => {
				this.accountsService.mergeAccountWithModel(result);
				this.spinnerService.hideSpinner(); //bizarre that this needs to be done as the service does not do it
			});
		}
	}

	getStatusString(account: Account): Promise<StatusString> {
		return undefined;
	}

	getConfigButtonLabel(account: Account): Promise<string> {
		return undefined;
	}

	isButtonDisabled(account: Account): Promise<boolean> {
		return Promise.resolve(false);
	}

	canDeleteIntegration(account: Account): Promise<boolean> {
		this.initServices();
		// must confirm removal
		return this.isSetupComplete(account).then(isSetup => {
			if (isSetup === false) {
				return true; //there was nothing setup, so delete does not need to be confirmed
			}
			//display a dialog to so the user can confirm the deletion
			const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.REMOVE_CMCOM_CONFIRMATION } });
			return dialogRef.afterClosed().toPromise().then(result => {
				return result;
			});
		});
	}

	isIntegrationStateOn(account: Account): Promise<boolean> {
		this.initServices();
		return this.adminService.getAccountTags(account.accountId).then(tags => {
			console.log("Here are the tags", tags)
			if (tags && tags.CMcom && tags.CMcom.enabled !== undefined)
				return tags.CMcom.enabled;
			else
				return false;
		})
			.catch(err => {
				return false;
			})
	}

	canBeSelected(account: Account): Promise<boolean> {
		return Promise.resolve(true);
	}

	completeSetup(accountId: string, connectionId: string, cmcomAccountId: string) {
		this.initServices();
		console.log("complete the integration");
		let dataChanges = {
			tags: {
				CMcom: {
					enabled: true,
					connectionId,
					accountId: cmcomAccountId
				}
			}
		}
		console.log("about to save the integration changes", dataChanges);

		return this.adminService.updateAccount(accountId, dataChanges);
	}
}
